/* import __COLOCATED_TEMPLATE__ from './reason-cards-list.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import type {
  ChartSeriesDefinition,
  Job,
  JobId,
} from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import type { JobGroupObject } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type { AttributeMapping } from 'embercom/services/reporting-underlying-data-service';
import { inject as service } from '@ember/service';

interface Signature {
  Args: {
    reportState: ReportState;
    selectedJob: Job | undefined;
    selectedJobId: JobId | undefined;
    availableJobs: Job[];
    onOpenSectionChange: (jobId: JobId) => void;
    taskSuggestions?: TaskSuggestion[];
    jobGroupObject: JobGroupObject;
  };
}

export default class ReasonCardsList extends Component<Signature> {
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare contentImportService: any;
  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  get chartDefinition() {
    let job = this.args.availableJobs[0];

    return {
      chart_series: job.chartSeries.map((series: ChartSeriesDefinition) => ({
        ...series,
      })),
      view_by: job.attributeId,
      view_by_display_limit: 25,
      view_by_other: false,
    };
  }

  @action
  jobById(id: JobId): Job {
    return this.args.availableJobs.find((job) => job.id === id) as Job;
  }

  @action
  conversationCountForJob(jobId: JobId) {
    let job = this.availableJobs.find((job) => job.jobId === jobId);
    if (!job || !this.dataResource.flatCombinedData) {
      return 0;
    }

    let data = this.dataResource.flatCombinedData[job.attributeToMatch];
    if (!data || !Array.isArray(data)) {
      return 0;
    }

    return data[0] as number;
  }

  @action
  suggestionCountForJob(jobId: JobId) {
    if (jobId === 'dataGaps') {
      return this.dataGapsSuggestionsCount;
    } else if (jobId === 'actionGaps') {
      return this.actionGapsSuggestionsCount;
    } else if (jobId === 'contentGaps') {
      return this.contentGapsSuggestionsCount;
    }
    return 0;
  }

  get dataGapsSuggestionsCount(): number {
    let suggestions = this.args.taskSuggestions || [];
    return suggestions.filter((suggestion) => suggestion.isDataGap).length;
  }

  get actionGapsSuggestionsCount(): number {
    let suggestions = this.args.taskSuggestions || [];
    return suggestions.filter((suggestion) => suggestion.isActionGap).length;
  }

  get contentGapsSuggestionsCount(): number {
    return this.contentImportService.aiContentReviewSummary.pending;
  }

  get availableCxAttributes(): AttributeMapping {
    return this.reportingUnderlyingDataService.getAttributeKnownValues(
      'conversation_custom_fields#customer_experience_attributes',
    );
  }

  get availableJobs(): { attributeToMatch: string; jobId: JobId }[] {
    if (!this.dataResource.flatCombinedData) {
      return [];
    }

    if (this.args.jobGroupObject.id === 'checkQuality') {
      // CX attributes come already mapped to their labels from the backend so we need to get the labels and keys
      // filter that to just the ones that are in the available jobs
      // then filter that to just the ones that are in the flatCombinedData
      let jobs = Object.entries(this.availableCxAttributes)
        .map(([value, text]) => {
          return {
            label: text,
            id: value,
          };
        })
        .filter((labelKey) => {
          return this.args.availableJobs.find((job) => job.attributesValues.includes(labelKey.id));
        });

      return jobs
        .filter((job) => {
          return Object.keys(this.dataResource.flatCombinedData).includes(job.label as string);
        })
        .map((job) => {
          return {
            attributeToMatch: job.label as string,
            jobId: this.args.availableJobs.find((availableJob) =>
              availableJob.attributesValues.includes(job.id),
            )?.id as string,
          };
        });
    } else {
      return this.args.availableJobs
        .filter((job) => {
          let jobAttributeValue = job.attributesValues[0];
          return Object.keys(this.dataResource.flatCombinedData).includes(jobAttributeValue);
        })
        .map((job) => {
          return {
            attributeToMatch: job.attributesValues[0],
            jobId: job.id,
          };
        });
    }
  }

  get jobsWithConversations(): JobId[] {
    let availableJobs = this.availableJobs;

    if (availableJobs.length === 0 || !this.dataResource.flatCombinedData) {
      return [];
    }

    let sortedJobs = availableJobs.sort((a, b) => {
      if (this.args.jobGroupObject.id === 'improveAnswers') {
        if (this.suggestionCountForJob(b.jobId) !== this.suggestionCountForJob(a.jobId)) {
          return this.suggestionCountForJob(b.jobId) - this.suggestionCountForJob(a.jobId);
        }
      }

      // Check if the data exists before accessing it
      let aData = this.dataResource.flatCombinedData[a.attributeToMatch];
      let bData = this.dataResource.flatCombinedData[b.attributeToMatch];

      if (!aData || !bData || !Array.isArray(aData) || !Array.isArray(bData)) {
        return 0;
      }

      return (bData[0] as number) - (aData[0] as number);
    });

    // Only call onOpenSectionChange if we have jobs
    if (sortedJobs.length > 0) {
      this.args.onOpenSectionChange(sortedJobs[0].jobId);
    }

    return sortedJobs.map((job) => job.jobId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::ReasonCardsList': typeof ReasonCardsList;
    'reporting/automation/ai-insights/optimize/reason-cards-list': typeof ReasonCardsList;
  }
}
